import type { CalendarDate } from '@internationalized/date'

import { envelopeBalance, type LedgerState } from './balances'
import type { Envelope, EnvelopeId } from './envelope'
import type { Xof } from './money'
import { daysElapsed, daysRemaining, periodLength, type PeriodBounds } from './period'

/**
 * État d'une enveloppe et alertes de rythme (spec §8, §15). Calculs déterministes, sans IA.
 * Un faible solde seul ne rend pas une enveloppe critique : on tient compte des réservations,
 * du rythme de dépense et des jours restants.
 */

export type EnvelopeHealth = 'normal' | 'watch' | 'critical'

export interface EnvelopeInsight {
  envelopeId: EnvelopeId
  health: EnvelopeHealth
  available: Xof
  spent: Xof
  daysRemaining: number
  /** Disponible par jour restant, arrondi à l'inférieur. */
  dailyAllowance: Xof
  /** Dépense projetée sur la période au rythme actuel, `null` si trop tôt pour estimer. */
  projectedSpend: Xof | null
  /** Part du financement de la période déjà dépensée (0 à 1+), `null` sans financement. */
  spentRatio: number | null
  reasons: InsightReason[]
}

/**
 * - overdrawn : disponible négatif ;
 * - empty : l'enveloppe a été financée et il n'y reste rien ;
 * - unfunded : rien n'y a encore été affecté (état neutre, pas une alerte) ;
 * - pace-exceeds-funds, high-spend-ratio : rythme de dépense trop rapide.
 */
export type InsightReason =
  'overdrawn' | 'empty' | 'unfunded' | 'pace-exceeds-funds' | 'high-spend-ratio'

/** Sous ce nombre de jours écoulés, le rythme n'est pas encore significatif. */
const MIN_DAYS_FOR_PACE = 3
/** Seuil de vigilance : part dépensée nettement en avance sur la part du mois écoulée. */
const SPEND_AHEAD_MARGIN = 0.15

export function envelopeInsight(
  envelope: Envelope,
  state: LedgerState,
  bounds: PeriodBounds,
  today: CalendarDate,
): EnvelopeInsight {
  const balance = envelopeBalance(state, envelope.id)
  const remaining = daysRemaining(bounds, today)
  const elapsed = daysElapsed(bounds, today)
  const length = periodLength(bounds)
  const funded =
    balance.openingBalance + balance.allocated + balance.transferredIn + balance.adjusted
  const spentRatio = funded > 0 ? balance.spent / funded : null

  const projectedSpend =
    elapsed >= MIN_DAYS_FOR_PACE ? Math.round((balance.spent / elapsed) * length) : null

  const reasons: InsightReason[] = []
  if (balance.available < 0) reasons.push('overdrawn')
  else if (funded <= 0 && balance.available === 0) reasons.push('unfunded')
  // Un disponible nul parce que l'argent est mis de côté pour une échéance n'est pas un manque.
  else if (
    balance.available === 0 &&
    balance.reserved === 0 &&
    remaining > 0 &&
    envelope.kind !== 'savings'
  )
    reasons.push('empty')

  const tracksPace = envelope.kind === 'variable' || envelope.kind === 'discretionary'
  if (tracksPace && projectedSpend !== null && funded > 0 && projectedSpend > funded) {
    reasons.push('pace-exceeds-funds')
  }
  if (tracksPace && spentRatio !== null && length > 0) {
    const elapsedRatio = elapsed / length
    if (spentRatio >= 0.8 && spentRatio - elapsedRatio > SPEND_AHEAD_MARGIN) {
      reasons.push('high-spend-ratio')
    }
  }

  const alerts = reasons.filter((reason) => reason !== 'unfunded')
  const health: EnvelopeHealth =
    alerts.includes('overdrawn') || alerts.includes('empty')
      ? 'critical'
      : alerts.length > 0
        ? 'watch'
        : 'normal'

  return {
    envelopeId: envelope.id,
    health,
    available: balance.available,
    spent: balance.spent,
    daysRemaining: remaining,
    dailyAllowance: remaining > 0 ? Math.floor(Math.max(balance.available, 0) / remaining) : 0,
    projectedSpend,
    spentRatio,
    reasons,
  }
}
